import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@ynap/button'
import BEMHelper from '@ynap/bem-helper'
import EmptyList from '@ynap/empty-list'
import Skeleton from '@ynap/skeleton'
import TileGroup from '../TileGroup/TileGroup'
import TileSkeleton from '../TileSkeleton/TileSkeleton'
import Tile from '../Tile/Tile'
import { redirectToLogin } from '@ynap/redirect-utils'
import track from 'react-tracking'
import LoyaltyCustomerCare from '@ynap/loyalty-customer-care'
import { CMV2Rule } from '@ycos/coremedia-renderer'

import getProgressBannerItem from '../utils/getProgressBannerItem'
import getFilteredTiles from '../utils/getFilteredTiles'
import LoyaltyHero from '../../LoyaltyHero/LoyaltyHero'
import { extractGreetingFromTime } from '../../../utils'
import { myaccountPageData, trackAccountNavigationClick } from '../../analytics'

const bem = new BEMHelper('MyAccountLanding')

@track()
class MyAccountLanding extends PureComponent {
    constructor(props) {
        super(props)
        this.state = { userSummary: props.userSummary, tierInfo: undefined }
        this.trackAction = this.trackAction.bind(this)
    }

    static propTypes = {
        tiles: PropTypes.array.isRequired,
        userSummary: PropTypes.object,
        className: PropTypes.string,
        skeletonTiles: PropTypes.number,
        messages: PropTypes.shape({
            myAccountLanding: PropTypes.shape({
                title: PropTypes.func.isRequired,
                pageError: PropTypes.func.isRequired,
                retryNow: PropTypes.func.isRequired,
            }).isRequired,
            pages: PropTypes.object.isRequired,
        }).isRequired,
        enableStoreCredit: PropTypes.bool,
        storeCreditExclusionList: PropTypes.array,
    }

    static defaultProps = {
        skeletonTiles: 6,
    }

    getUserSummary() {
        const { locale } = this.props

        if (window && window.SF && window.SF.getUserSummary) {
            return window.SF.getUserSummary()
                .then((userSummary) => {
                    if (userSummary != null) {
                        return this.setState({ userSummary })
                    }
                    return redirectToLogin(locale.id)
                })
                .catch(() => {
                    return redirectToLogin(locale.id)
                })
        }
    }

    getTierInfo() {
        if (window && window.SF && window.SF.getTierInfo) {
            return window.SF.getTierInfo(true).then((tierInfo) => {
                if (tierInfo != null) {
                    return this.setState({ tierInfo })
                }
            })
        }
    }

    @track(
        myaccountPageData({
            name: 'landing page',
            subCategory1: 'landing page',
        })
    )
    componentDidMount() {
        const { country, content, fetchContent, userSummary } = this.props
        if (country && !content) {
            fetchContent('myaccount-landing-content')
        }
        if (!userSummary) {
            this.getUserSummary()
        } else {
            this.setState({ userSummary: this.props.userSummary })
        }

        this.getTierInfo()
    }

    componentDidUpdate() {
        const { userSummary } = this.props
        const { tierInfo } = this.state

        if (userSummary?.loyaltySegment && !tierInfo) {
            this.getTierInfo()
        }
    }

    @track((props, state, [section]) => trackAccountNavigationClick(section))
    trackAction() {}

    render() {
        const {
            className,
            tiles,
            messages,
            skeletonTiles,
            content,
            dir,
            isFetchingContent,
            enableStoreCredit,
            storeCreditExclusionList,
            locale,
        } = this.props
        const { userSummary, tierInfo } = this.state
        const isRTL = dir === 'rtl'
        const { loyaltyConsent, loyaltySegment } = userSummary || {}
        let heroBanner = (
            <Fragment>
                <Skeleton className={bem('greetingSkeleton')} count={2} />
            </Fragment>
        )

        // Currently only for MRP and NAP
        const tierExpansionEnabled = userSummary && 'loyaltyConsent' in userSummary
        const showLoyaltyBanner = Boolean(!isFetchingContent && tierExpansionEnabled && tierInfo)
        if (showLoyaltyBanner) {
            const heroBannerItem = getProgressBannerItem(content, loyaltyConsent, loyaltySegment)
            const textStyle = heroBannerItem?.ynapParameter?.toLowerCase()
            // CMV2 return pictures inside media prop and this rule is applied so we still have pictures prop
            if (heroBannerItem) CMV2Rule({ item: heroBannerItem })
            heroBanner = (
                <LoyaltyHero
                    locale={locale}
                    userFirstName={userSummary.firstName}
                    userLastName={userSummary.lastName}
                    userTitle={userSummary.personTitle}
                    messages={messages}
                    loyaltyConsent={loyaltyConsent === 'true'}
                    picturesAndMedia={heroBannerItem?.pictures}
                    currentTier={loyaltySegment}
                    nextTier={tierInfo.nextTier}
                    progress={{
                        value: tierInfo.progress,
                        intervalMin: tierInfo.lowerThreshold,
                        intervalMax: tierInfo.upperThreshold,
                        status: tierInfo.tierStatus,
                    }}
                    amountSpent={tierInfo.currentSpending}
                    amountPending={tierInfo.pendingSpending}
                    isStaff={tierInfo.isStaff}
                    isTierExpiring={tierInfo.actionStatus === 'EXPIRING'}
                    colorScheme={textStyle}
                    showRewardsPageLink
                    isRTL={isRTL}
                />
            )
        }

        if (userSummary && !('loyaltyConsent' in userSummary)) {
            heroBanner = (
                <Fragment>
                    <h3 className={bem('greeting')}>
                        {extractGreetingFromTime(
                            messages,
                            null,
                            userSummary.lastName,
                            userSummary.personTitle,
                            userSummary.firstName
                        )}
                    </h3>
                    <h2 className={bem('title')}>{messages.myAccountLanding.title()}</h2>
                </Fragment>
            )
        }

        let tilesToShow
        if (userSummary) {
            tilesToShow = getFilteredTiles(tiles, {
                enableStoreCredit,
                storeCreditExclusionList,
                locale,
                userSummary,
            })
        }
        return (
            <div className={bem(null, className)}>
                {userSummary === null ? (
                    <EmptyList
                        className={bem('empty')}
                        variant={'myAccountError'}
                        title={messages.myAccountLanding.pageError()}
                    >
                        <Button type="primary-link" className={bem('emptyLink')} onClick={this.getUserSummary}>
                            {messages.myAccountLanding.retryNow()}
                        </Button>
                    </EmptyList>
                ) : (
                    <Fragment>
                        <div className={bem('banner', showLoyaltyBanner ? 'loyalty' : 'greeting')}>{heroBanner}</div>
                        <TileGroup className={bem('tiles')}>
                            {tilesToShow ? (
                                tilesToShow.map((tile) => {
                                    const { id, title, description, descriptionSmall, customClass } = tile
                                    return (
                                        <Tile
                                            {...tile}
                                            key={id}
                                            title={title || messages.pages[id].title()}
                                            description={description || messages.pages[id].description()}
                                            descriptionSmall={
                                                descriptionSmall || messages.pages[id]?.descriptionSmall?.()
                                            }
                                            className={bem('tile', [id, customClass])}
                                            trackClick={this.trackAction}
                                            loyaltySegment={loyaltySegment}
                                        />
                                    )
                                })
                            ) : (
                                <Fragment>
                                    {Array(skeletonTiles)
                                        .fill()
                                        .map((o, i) => (
                                            <TileSkeleton key={i} />
                                        ))}
                                </Fragment>
                            )}
                        </TileGroup>
                        {tierExpansionEnabled && (
                            <LoyaltyCustomerCare
                                className={bem('customerCare')}
                                tier={loyaltyConsent === 'true' && loyaltySegment ? loyaltySegment : 'guest'}
                                locale={locale}
                                messages={messages}
                            />
                        )}
                    </Fragment>
                )}
            </div>
        )
    }
}

export default MyAccountLanding
